import React, { useState } from "react";
import Button from "@mui/material/Button";
import StandardModal from "../../../components/modales/StandardModal";
import VisibilityOutlined from "@mui/icons-material/VisibilityOutlined";
import { type DataTableColumn } from "@routes-plan/ui";
import { ModalTable, RowAction } from "../components/ReportLayout";
import DeletedRecordDetailModal from "./DeletedRecordDetailModal";
import { useT } from "../../../i18n";
import { companyLocale, formatCurrency as companyMoney } from '../../../utils/format/currency';

export interface DeletedLoanSummary {
  backupId: number;
  originalLoanId: number;
  value: number;
  totalValue: number;
  insuranceCharge: number;
  deletedByName: string;
  deletedAt: string;
}

interface DeletedLoansModalProps {
  open: boolean;
  onClose: () => void;
  clientName: string | null;
  loans: DeletedLoanSummary[];
  showAlert: (msg: string, severity?: "error" | "info" | "success" | "warning") => void;
}

const formatCurrency = (value: unknown) => companyMoney(Number(value || 0));

const formatDateTime = (value?: string): string => {
  if (!value) return "";
  const d = new Date(String(value).replace(" ", "T"));
  if (isNaN(d.getTime())) return value;
  return d.toLocaleString(companyLocale());
};

const DeletedLoansModal: React.FC<DeletedLoansModalProps> = ({
  open, onClose, clientName, loans, showAlert,
}) => {
  const t = useT();
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailBackupId, setDetailBackupId] = useState<number | null>(null);

  const rows = loans.map((l, i) => ({
    id: `loan-${l.backupId}-${i}`,
    backupId: l.backupId,
    originalLoanId: l.originalLoanId,
    value: l.value,
    totalValue: l.totalValue,
    insuranceCharge: l.insuranceCharge,
    deletedByName: l.deletedByName,
    deletedAt: l.deletedAt,
  }));

  type LoanRow = (typeof rows)[number];
  const money = (field: "value" | "totalValue" | "insuranceCharge", header: string): DataTableColumn<LoanRow> => ({
    id: field,
    header,
    align: "right",
    sortable: true,
    value: (r) => Number(r[field] || 0),
    render: (r) => formatCurrency(r[field]),
  });
  const columns: DataTableColumn<LoanRow>[] = [
    { id: "originalLoanId", header: t("auditReports.deletedLoans.colLoanId"), value: (r) => r.originalLoanId, sortable: true },
    money("value", t("auditReports.deletedLoans.colValue")),
    money("totalValue", t("auditReports.deletedLoans.colTotalValue")),
    money("insuranceCharge", t("auditReports.deletedLoans.colInsurance")),
    { id: "deletedByName", header: t("auditReports.deletedBy"), value: (r) => r.deletedByName, hideBelow: "sm" },
    { id: "deletedAt", header: t("auditReports.dateTime"), value: (r) => r.deletedAt, render: (r) => formatDateTime(String(r.deletedAt ?? "")), csv: (r) => formatDateTime(String(r.deletedAt ?? "")), sortable: true },
  ];

  return (
    <>
      <StandardModal
        open={open}
        onClose={onClose}
        title={t("auditReports.deletedLoans.title")}
        subtitle={clientName || undefined}
        maxWidth={900}
        actions={
          <>
            <span />
            <Button variant="outlined" onClick={onClose}>
              {t("common.close")}
            </Button>
          </>
        }
      >
        <ModalTable
          rows={rows}
          columns={columns}
          getRowId={(r) => r.id}
          searchable={false}
          ariaLabel={t("auditReports.deletedLoans.title")}
          rowActions={(row) => (
            <RowAction
              label={t("auditReports.deletedLoans.view")}
              icon={<VisibilityOutlined fontSize="small" />}
              onClick={() => {
                setDetailBackupId(Number(row.backupId));
                setDetailOpen(true);
              }}
            />
          )}
        />
      </StandardModal>

      <DeletedRecordDetailModal
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        recordType="lend"
        backupId={detailBackupId}
        showAlert={showAlert}
      />
    </>
  );
};

export default DeletedLoansModal;
